import { guardianKeys } from '@/features/guardian/queries/query-keys';
import { fetchGuardianMe, type GuardianProfile } from '@/features/guardian/services/get-guardian';
import { ONBOARDING_TOTAL_STEPS } from '@/features/onboarding/hooks/use-onboarding';
import type { ApiError } from '@/libs/api/types';
import { queryClient } from '@/libs/tanstack-query/query-client';
import type { GuardianStackParamList } from '@/navigations/types';
import { cancelAccountDeletion } from '@/services/schedule-account-deletion';
import { useSessionStore } from '@/stores/session-store';
import type { NavigatorScreenParams } from '@react-navigation/native';

/** Extra attempts (on top of the first) for transient profile-fetch failures. */
const PROFILE_FETCH_MAX_RETRIES = 2;
/** Exponential backoff base between profile-fetch retries. */
const PROFILE_FETCH_BACKOFF_MS = 400;

/**
 * A 404 means the backend has no guardian doc yet — the user genuinely needs to onboard. This is
 * terminal (retrying won't conjure the doc), unlike a network/timeout/5xx blip which should retry.
 */
function isNotFound(error: unknown): boolean {
  return (error as ApiError | undefined)?.status === 404;
}

/**
 * If the signed-in guardian's account is on a scheduled-deletion hold, logging back in
 * within the 30-day window cancels it (BR-03 / TC-07). Called right after the profile is
 * fetched on entry. Silently guarded — a failed cancel must never block the login landing;
 * it will be retried on the next login. On success the cached profile is flipped to
 * `active` so the app doesn't re-trigger.
 */
async function cancelPendingDeletionIfNeeded(
  uid: string | undefined,
  profile: GuardianProfile,
): Promise<void> {
  if (profile.status !== 'pending_deletion') return;

  try {
    await cancelAccountDeletion();
    queryClient.setQueryData(guardianKeys.profile(uid), {
      ...profile,
      status: 'active',
    });
  } catch {
    // Ignore — keep the user moving; the next login will retry the cancellation.
  }
}

/**
 * Decides where a guardian should land inside the `Guardian` stack, from their
 * saved onboarding progress (fetched once and cached so the target screen reuses
 * it without a second request):
 * - finished (`onboarding.status === 'completed'`) → `GuardianDashboard`;
 * - otherwise → `Onboarding`, resumed at the recorded step (`onboarding.currentStep`).
 *
 * Error handling is deliberately NOT "default to onboarding": a genuinely-not-yet-onboarded
 * guardian is a *successful* 200 (`onboarding` absent / not `completed`), so it never reaches the
 * `catch`. The `catch` only fires on real failures, and blindly routing those to `Onboarding` was
 * bouncing already-onboarded guardians out of their dashboard on a transient blip (common on web,
 * where a reload wipes the cache and the fetch runs live on the nav path). Instead:
 * - transient failures (network / timeout / 5xx) are retried a couple of times;
 * - a 404 (no guardian doc yet) is the one error that legitimately means "onboard";
 * - any other unrecoverable failure lands on `GuardianDashboard`, whose own queries refetch and
 *   self-heal once the backend recovers — never a forced trip back through onboarding.
 */
export async function resolveGuardianEntry(): Promise<
  NavigatorScreenParams<GuardianStackParamList>
> {
  const uid = useSessionStore.getState().user?.uid;

  let profile: GuardianProfile;
  try {
    // `fetchQuery` dedupes concurrent callers (Splash + the auth guards can all resolve the entry
    // at once) and seeds the cache the dashboard reads. Override the global `retry: 0` here so a
    // single cold-DNS / token-refresh blip doesn't decide the landing screen.
    profile = await queryClient.fetchQuery({
      queryKey: guardianKeys.profile(uid),
      queryFn: () => fetchGuardianMe(),
      retry: (failureCount, error) =>
        !isNotFound(error) && failureCount < PROFILE_FETCH_MAX_RETRIES,
      retryDelay: attempt => PROFILE_FETCH_BACKOFF_MS * 2 ** attempt,
    });
  } catch (error) {
    // No guardian doc on the backend → the user really does need to onboard.
    if (isNotFound(error)) {
      return { screen: 'Onboarding' };
    }
    // Transient / server-side failure after retries. Do NOT force onboarding — a possibly-onboarded
    // guardian must not be kicked out of their dashboard. (A revoked token triggers a sign-out
    // teardown in the axios error interceptor, which supersedes this landing.)
    return { screen: 'GuardianDashboard' };
  }

  // Re-login within the 30-day hold cancels a scheduled deletion.
  await cancelPendingDeletionIfNeeded(uid, profile);

  const onboarding = profile.onboarding;
  if (onboarding?.status === 'completed') {
    return { screen: 'GuardianDashboard' };
  }

  // `onboarding.currentStep` is the 1-based step to resume at; clamp into range.
  const initialStep = Math.min(
    Math.max((onboarding?.currentStep ?? 1) - 1, 0),
    ONBOARDING_TOTAL_STEPS - 1,
  );
  return { screen: 'Onboarding', params: { initialStep } };
}
